/**
 * 应用初始化管理器
 *
 * 职责：
 * 1. 显式声明初始化步骤和依赖关系
 * 2. 提供统一的错误处理和重试机制
 * 3. 管理初始化状态，便于监控和调试
 * 4. 确保初始化步骤按正确顺序执行
 */

import { ref, computed } from 'vue'
import { dbManager } from '@/indexdb/chat'
import { useConversationStore } from '@/stores/conversation'
import { useIMStore } from '@/stores/im'
import { showToast } from 'vant'
import { t } from '@/locales'

/**
 * 初始化步骤枚举
 */
export const InitStep = {
  IDLE: 'idle',                     // 未开始
  DATABASE: 'database',              // 数据库初始化
  CONVERSATIONS: 'conversations',    // 会话列表加载
  IM_CONNECTION: 'im_connection',    // IM 连接
  COMPLETED: 'completed',            // 全部完成
  FAILED: 'failed',                  // 失败
}

/**
 * 初始化步骤配置
 * 定义每个步骤的依赖关系、执行函数、错误处理策略
 */
const INIT_STEPS = [
  {
    name: InitStep.DATABASE,
    displayName: 'Database Initialization',
    dependencies: [],
    required: true,  // 必需步骤，失败则终止整个流程
    execute: async (context) => {
      const { userId, memberId } = context
      await dbManager.initDatabase(userId, memberId)
    },
    onError: (error) => {
      console.error('[InitManager] Database initialization failed:', error)
      showToast(t('common.error.dbInitFailed'))
    }
  },
  {
    name: InitStep.CONVERSATIONS,
    displayName: 'Conversations Loading',
    dependencies: [InitStep.DATABASE],  // 依赖数据库
    required: false,  // 非必需，失败后可继续
    execute: async () => {
      const conversationStore = useConversationStore()
      // 先从缓存加载（快速显示）
      await conversationStore.initConversations()
      // 后台刷新最新数据
      try {
        await conversationStore.refreshConversationsFromAPI()
      } catch (error) {
        // 刷新失败不影响主流程，静默处理
        console.error('[AppInitManager] Background refresh failed:', error)
      }
    },
    onError: (error) => {
      console.error('[InitManager] Conversations loading failed:', error)
      showToast(t('common.error.loadConversationsFailed'))
    }
  },
  {
    name: InitStep.IM_CONNECTION,
    displayName: 'IM Connection',
    dependencies: [InitStep.DATABASE],  // 依赖数据库（会话列表是可选的）
    required: false,  // 非必需，失败后应用仍可用（只是不能收发消息）
    execute: async () => {
      const imStore = useIMStore()
      await imStore.initIM()
    },
    onError: (error) => {
      console.error('[InitManager] IM connection failed:', error)
      showToast(t('common.error.imConnectFailed'))
    }
  }
]

class AppInitManager {
  constructor() {
    // 当前步骤
    this.currentStep = ref(InitStep.IDLE)

    // 已完成的步骤
    this.completedSteps = ref(new Set())

    // 失败的步骤
    this.failedSteps = ref(new Map())  // Map<stepName, error>

    // 是否正在初始化
    this.isInitializing = ref(false)

    // 初始化上下文（用于传递数据）
    this.context = ref({})
  }

  /**
   * 计算属性：初始化进度百分比
   */
  get progress() {
    return computed(() => {
      const total = INIT_STEPS.length
      const completed = this.completedSteps.value.size
      return Math.round((completed / total) * 100)
    })
  }

  /**
   * 计算属性：初始化是否完成
   */
  get isCompleted() {
    return computed(() => {
      return this.currentStep.value === InitStep.COMPLETED
    })
  }

  /**
   * 计算属性：是否有错误
   */
  get hasErrors() {
    return computed(() => {
      return this.failedSteps.value.size > 0
    })
  }

  /**
   * 开始初始化流程
   * @param {Object} options - 初始化选项
   * @param {string} options.userId - 用户 ID
   * @param {string} options.memberId - 会员 ID
   */
  async initialize(options) {
    const { userId, memberId } = options

    if (!userId) {
      throw new Error('User ID is required for initialization')
    }

    // 防止重复初始化
    if (this.isInitializing.value) {
      console.warn('[InitManager] Already initializing')
      return
    }

    // 如果已经初始化完成且用户ID相同，跳过重复初始化（用于热更新场景）
    if (this.isCompleted.value && this.context.value.userId === userId) {
      return
    }

    try {
      this.isInitializing.value = true
      this.currentStep.value = InitStep.IDLE
      this.completedSteps.value.clear()
      this.failedSteps.value.clear()
      this.context.value = { userId, memberId }

      console.log('[InitManager] Starting initialization...', options)

      // 按顺序执行所有步骤
      for (const step of INIT_STEPS) {
        // 检查依赖是否满足
        if (!this._checkDependencies(step)) {
          console.warn(`[InitManager] Skipping step "${step.name}" due to unmet dependencies`)
          continue
        }

        // 执行步骤
        await this._executeStep(step)

        // 如果是必需步骤且失败了，终止流程
        if (step.required && this.failedSteps.value.has(step.name)) {
          console.error(`[InitManager] Required step "${step.name}" failed, aborting initialization`)
          this.currentStep.value = InitStep.FAILED
          return
        }
      }

      // 所有步骤完成
      this.currentStep.value = InitStep.COMPLETED
      console.log('[InitManager] Initialization completed successfully')

    } catch (error) {
      console.error('[InitManager] Initialization failed:', error)
      this.currentStep.value = InitStep.FAILED
      showToast(t('common.error.appInitFailed'))
    } finally {
      this.isInitializing.value = false
    }
  }

  /**
   * 检查步骤的依赖是否满足
   * @param {Object} step - 步骤配置
   * @returns {boolean}
   * @private
   */
  _checkDependencies(step) {
    return step.dependencies.every(dep => this.completedSteps.value.has(dep))
  }

  /**
   * 执行单个步骤
   * @param {Object} step - 步骤配置
   * @private
   */
  async _executeStep(step) {
    this.currentStep.value = step.name
    console.log(`[InitManager] Executing step: ${step.displayName}`)

    try {
      await step.execute(this.context.value)
      this.completedSteps.value.add(step.name)
      console.log(`[InitManager] Step "${step.displayName}" completed`)
    } catch (error) {
      this.failedSteps.value.set(step.name, error)

      if (step.onError) {
        step.onError(error)
      }

      console.error(`[InitManager] Step "${step.displayName}" failed:`, error)
    }
  }

  /**
   * 重试失败的步骤
   * @param {string} stepName - 步骤名称
   */
  async retryStep(stepName) {
    const step = INIT_STEPS.find(s => s.name === stepName)
    if (!step) {
      throw new Error(`Unknown step: ${stepName}`)
    }

    // 移除失败记录
    this.failedSteps.value.delete(stepName)

    // 重新执行
    await this._executeStep(step)
  }

  /**
   * 重试所有失败的步骤
   */
  async retryAll() {
    const failedStepNames = Array.from(this.failedSteps.value.keys())

    for (const stepName of failedStepNames) {
      await this.retryStep(stepName)
    }
  }

  /**
   * 重置管理器状态
   */
  reset() {
    this.currentStep.value = InitStep.IDLE
    this.completedSteps.value.clear()
    this.failedSteps.value.clear()
    this.isInitializing.value = false
    this.context.value = {}
  }

  /**
   * 获取初始化状态摘要
   * @returns {Object}
   */
  getStatusSummary() {
    return {
      currentStep: this.currentStep.value,
      progress: this.progress.value,
      isInitializing: this.isInitializing.value,
      isCompleted: this.isCompleted.value,
      hasErrors: this.hasErrors.value,
      completedSteps: Array.from(this.completedSteps.value),
      failedSteps: Array.from(this.failedSteps.value.keys()),
    }
  }
}

// 导出单例
export const appInitManager = new AppInitManager()

export default AppInitManager
